import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Dropdown from './Dropdown';
import CustomButton from '../OnboardingComponents/CustomButton';
import CustomCheckbox from '../OnboardingComponents/CustomCheckbox';
import { ICONS } from '../../constants/icons';

/** Normalize FilterDropdown select onChange values to a single string. */
export const toSingleFilterValue = next => {
  if (Array.isArray(next)) return String(next[0] || '');
  return String(next ?? '');
};

/* ------------------------------------------------------------------------
 * Section helpers
 * ---------------------------------------------------------------------- */

const isActiveValue = (filter) => {
  const { type, value, defaultValue } = filter;
  if (type === 'select' || type === 'status') {
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  }
  if (type === 'sortBy') {
    return Boolean(value) && value !== defaultValue;
  }
  if (type === 'sortDirection') {
    return Boolean(value) && value !== (defaultValue || 'asc');
  }
  return Boolean(value);
};

const clearFilterValue = (filter) => {
  const { type, defaultValue } = filter;
  if (type === 'select' || type === 'status') {
    return defaultValue ?? '';
  }
  if (type === 'sortBy') return defaultValue ?? '';
  if (type === 'sortDirection') return defaultValue ?? 'asc';
  return defaultValue ?? '';
};

/* ------------------------------------------------------------------------
 * Section renderers — select filters are always single-select
 * ---------------------------------------------------------------------- */

const SelectSection = ({ filter }) => {
  const [query, setQuery] = useState('');
  const selected = Array.isArray(filter.value)
    ? String(filter.value[0] || '')
    : filter.value;

  const options = useMemo(() => {
    const opts = filter.options || [];
    if (!query.trim()) return opts;
    return opts.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));
  }, [filter.options, query]);

  const toggle = (value) => {
    const next = selected === value ? (filter.defaultValue || '') : value;
    filter.onChange(next);
  };

  return (
    <div className="filter-panel__section">
      <div className="filter-panel__section-head">
        <span className="filter-panel__section-title">{filter.label}</span>
        {selected && (
          <button
            type="button"
            className="filter-panel__section-link"
            onClick={() => filter.onChange(filter.defaultValue || '')}
          >
            Clear
          </button>
        )}
      </div>
      {filter.searchable && (
        <div className="filter-panel__search">
          <img src={ICONS.search} alt="" className="filter-panel__search-icon" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${filter.label.toLowerCase()}...`}
          />
        </div>
      )}
      <div className="filter-panel__options">
        {options.length === 0 && (
          <p className="filter-panel__empty">No matches</p>
        )}
        {options.map((opt) => (
          <div
            key={opt.value}
            className="filter-panel__option"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggle(opt.value);
            }}
          >
            <CustomCheckbox
              label={opt.label}
              checked={selected === opt.value}
              onChange={() => {}}
              checkIconSrc={ICONS.check}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusSection = ({ filter }) => {
  const selected = Array.isArray(filter.value)
    ? String(filter.value[0] || '')
    : String(filter.value || '');

  const toggle = (value) => {
    const next = selected === value ? (filter.defaultValue || '') : value;
    filter.onChange(next);
  };

  return (
    <div className="filter-panel__section">
      <div className="filter-panel__section-head">
        <span className="filter-panel__section-title">{filter.label}</span>
        {selected && (
          <button
            type="button"
            className="filter-panel__section-link"
            onClick={() => filter.onChange(filter.defaultValue || '')}
          >
            Clear
          </button>
        )}
      </div>
      <div className="filter-panel__status-list">
        {(filter.options || []).map((opt) => (
          <label
            key={opt.value}
            className={`filter-panel__status filter-panel__status--${opt.tone || 'grey'} ${
              selected === opt.value ? 'filter-panel__status--active' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={selected === opt.value}
              onChange={() => toggle(opt.value)}
            />
            <span className="filter-panel__status-dot" />
            <span className="filter-panel__status-label">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const SortBySection = ({ filter }) => (
  <div className="filter-panel__section">
    <span className="filter-panel__section-title">{filter.label}</span>
    <div className="filter-panel__radio-list">
      {(filter.options || []).map((opt) => (
        <button
          type="button"
          key={opt.value}
          className={`filter-panel__radio ${
            filter.value === opt.value ? 'filter-panel__radio--active' : ''
          }`}
          onClick={() => filter.onChange(opt.value)}
        >
          <span className="filter-panel__radio-dot" />
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  </div>
);

const SortDirectionSection = ({ filter }) => (
  <div className="filter-panel__section">
    <span className="filter-panel__section-title">{filter.label}</span>
    <div className="filter-panel__segmented">
      <button
        type="button"
        className={`filter-panel__segmented-btn ${
          filter.value === 'asc' ? 'filter-panel__segmented-btn--active' : ''
        }`}
        onClick={() => filter.onChange('asc')}
      >
        Ascending
      </button>
      <button
        type="button"
        className={`filter-panel__segmented-btn ${
          filter.value === 'desc' ? 'filter-panel__segmented-btn--active' : ''
        }`}
        onClick={() => filter.onChange('desc')}
      >
        Descending
      </button>
    </div>
  </div>
);

const SECTION_COMPONENTS = {
  select: SelectSection,
  status: StatusSection,
  sortBy: SortBySection,
  sortDirection: SortDirectionSection,
};

/* ------------------------------------------------------------------------
 * FilterDropdown
 * ---------------------------------------------------------------------- */

const FilterDropdown = ({
  label = 'Filter',
  options,
  selected,
  onChange,
  filters: filtersProp,
  className,
}) => {
  const filters = useMemo(() => {
    if (filtersProp) return filtersProp;
    if (options) {
      return [
        {
          key: 'legacy',
          type: 'select',
          label: 'Category',
          options,
          value: Array.isArray(selected)
            ? String(selected[0] || '')
            : selected || '',
          defaultValue: '',
          onChange,
        },
      ];
    }
    return [];
  }, [filtersProp, options, selected, onChange]);

  const activeCount = useMemo(
    () => filters.reduce((acc, f) => {
      if (f.type === 'select' || f.type === 'status') {
        return acc + (Array.isArray(f.value) ? Number(f.value.length > 0) : Number(!!f.value));
      }
      return acc + (isActiveValue(f) ? 1 : 0);
    }, 0),
    [filters]
  );

  const handleClearAll = () => {
    filters.forEach((f) => f.onChange(clearFilterValue(f)));
  };

  return (
    <Dropdown
      align="right"
      className={className}
      trigger={
        <CustomButton
          variant="secondary"
          size="md"
          trailingIconSrc={ICONS.chevronDown}
          trailingIconAlt=""
        >
          <span className="filter-trigger">
            <span>{label}</span>
            {activeCount > 0 && (
              <span className="filter-trigger__count">{activeCount}</span>
            )}
          </span>
        </CustomButton>
      }
    >
      <div className="filter-panel" onClick={(e) => e.stopPropagation()}>
        <div className="filter-panel__header">
          <span className="filter-panel__heading">Filters</span>
          {activeCount > 0 && (
            <button
              type="button"
              className="filter-panel__clear"
              onClick={handleClearAll}
            >
              Clear all
            </button>
          )}
        </div>
        <div className="filter-panel__body">
          {filters.map((filter) => {
            const Section = SECTION_COMPONENTS[filter.type];
            if (!Section) return null;
            return <Section key={filter.key} filter={filter} />;
          })}
        </div>
      </div>
    </Dropdown>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  tone: PropTypes.string,
});

const filterShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['select', 'status', 'sortBy', 'sortDirection']).isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(optionShape),
  /** @deprecated Multi-select is no longer supported; selects are always single. */
  multiple: PropTypes.bool,
  searchable: PropTypes.bool,
  defaultValue: PropTypes.any,
});

FilterDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(optionShape),
  selected: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  onChange: PropTypes.func,
  filters: PropTypes.arrayOf(filterShape),
  className: PropTypes.string,
};

FilterDropdown.defaultProps = {
  label: 'Filter',
  options: null,
  selected: '',
  onChange: () => {},
  filters: null,
  className: '',
};

export default FilterDropdown;
